import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import "./ProductsList.scss";
import {
  TABLE_PRODUCTS_LIST,
  PRODUCTS_LIST_ENDPOINT,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { loadAllProducts } from "./ProductsList.actionCreators";
import { setAllProductsLoadingState } from "./ProductsList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import ProductAction from "./components/ProductAction/ProductAction";
import ProductId from "./components/ProductId/ProductId";
import Country from "./components/Country/Country";
import GeneralInfo from "./components/GeneralInfo/GeneralInfo";
import { columnHeader, isEmpty } from "@utils/utils";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CustomColumnFilter from "@shared/baseUI/CustomColumnFilter/CustomColumnFilter";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createProduct,
    categories,
    subCategories,
    countries,
  } = props;

  const [status, setStatus] = useState(2);
  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const dataURL = PRODUCTS_LIST_ENDPOINT;
  const dispatch = useDispatch();
  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [];

  columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      Cell: ({ cell: { value } }) => (
        <ProductId
          data={"#" + value.productId}
          id={value.productId}
          status={value.status}
          updateRefeshStatus={updateRefeshStatus}
          countries={countries}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("COUNTRY"),
      title: "COUNTRY",
      accessor: "country",
      Cell: ({ cell: { value } }) => <Country data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("COUNTRY")}
          placeholder={t("SEARCH")}
          name="country"
          labelClass="products-list__country-label"
          containerClass="products-list__country-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={countries}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("CATEGORY"),
      title: "CATEGORY",
      accessor: "category",
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("CATEGORY")}
          placeholder={t("SEARCH")}
          name="category"
          labelClass="products-list__category-label"
          containerClass="products-list__category-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={categories}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("SUB_CATEGORY"),
      title: "SUB_CATEGORY",
      accessor: "subCategory",
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("SUB_CATEGORY")}
          placeholder={t("SEARCH")}
          name="subCategory"
          labelClass="products-list__subCategory-label"
          containerClass="products-list__subCategory-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={subCategories}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("NAME"),
      title: "NAME",
      accessor: "name",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} />,
    },
    {
      Header: t("PRODUCT_CODE"),
      title: "PRODUCT_CODE",
      accessor: "productCode",
      disableFilters: false,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} />,
    },
    {
      Header: t("OPERATIONS"),
      title: "OPERATIONS",
      accessor: "operation",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="operation" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <ProductAction
          data={value}
          column={column}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
          countries={countries}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const productsListByIds = useSelector((state) => state.productsList.byId);
  const productsListAllIds = useSelector((state) => state.productsList.allIds);
  const filteredRecords = useSelector(
    (state) => state.productsList.filteredRecords
  );

  const isProductsListLoading = useSelector(
    (state) => state.productsList.isLoading
  );
  useEffect(() => {
    setLoading(isProductsListLoading);
  }, [isProductsListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    let category = [];
    let subCategory = [];
    let country = [];
    if (appliedFilters !== undefined) {
      const categoryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "category"
      );
      category = categoryFilter?.value ?? [];
      const subCategoryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "subCategory"
      );
      subCategory = subCategoryFilter?.value ?? [];

      const countryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "country"
      );
      country = countryFilter?.value ?? [1, 2, 3, 4, 5, 6, 7, 8];
    }
    // Set the loading state
    setLoading(true);
    dispatch(setAllProductsLoadingState());
    dispatch(
      loadAllProducts({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        appliedFilters,
        status,
        category,
        subCategory,
        country,
      })
    );
  };

  useEffect(() => {
    if (productsListAllIds.length > 0) {
      const responseData = productsListAllIds.map((id) => {
        const tempData = { ...productsListByIds[id] };
        tempData.action = {
          id: tempData.id,
          status: tempData.isActive,
        };
        tempData.id = {
          productId: tempData.id,
          status: tempData.isActive,
        };
        tempData.country = {
          countryInfo: tempData.country,
          status: tempData.isActive,
        };
        tempData.category = {
          name: t(tempData.category.name),
          status: tempData.isActive,
        };
        tempData.subCategory = {
          name: t(tempData.subCategory.name),
          status: tempData.isActive,
        };
        tempData.name = {
          name: tempData.name,
          status: tempData.isActive,
        };
        tempData.productCode = {
          name: tempData.productCode,
          status: tempData.isActive,
        };
        tempData.operation = {
          canDelivery: tempData.canDelivery,
          canEvacuate: tempData.canEvacuate,
          canEdit: tempData.canEdit,
          status: tempData.isActive,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, productsListAllIds, productsListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = (label) => {
    let statusNew = status === 1 ? 2 : 1;
    setStatus(statusNew);
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: requestData.appliedFilters,
      });
    }
  }, [status]);

  return (
    <EncloseTable
      type={TABLE_PRODUCTS_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={30}
      createNewAction={createProduct}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      setCurrentPageToFirst={setCurrentPageToFirst}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_BUTTON,
          label: "PRODUCTS_FILTER_ACTIVE_ONLY",
          name: "users",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
